import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Loader2, Heart, AlertTriangle, CheckCircle2, X } from 'lucide-react';
import { apiClient } from '../api/client';

/**
 * Parent Approve Hearts (Magic Link)
 * ──────────────────────────────────
 * The parent lands here from the SMS/email after their teen taps
 * "Ask my parent" on a Hearts pack.
 *
 *  URL: /parent-approve-hearts?token=…
 *  Flow:
 *    1. GET /api/billing/hearts-approval/preview → render summary.
 *    2. Parent taps Approve → POST /approve → backend creates Stripe
 *       Checkout Session → frontend redirects to res.url.
 *    3. Or parent taps Decline → POST /decline → polite "closed" screen.
 */

const PURPLE = '#7B4FD8';
const ORANGE = '#F5831F';

const ParentApproveHearts = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const [phase, setPhase] = useState('loading'); // loading | invalid | ready | approving | declined
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!token) {
      setPhase('invalid');
      setError('This approval link is missing its token.');
      return;
    }
    let cancelled = false;
    apiClient.request(`/api/billing/hearts-approval/preview?token=${encodeURIComponent(token)}`)
      .then((res) => {
        if (cancelled) return;
        setData(res);
        if (res.status === 'pending') setPhase('ready');
        else if (res.status === 'approved') setPhase('already-approved');
        else if (res.status === 'declined') setPhase('declined');
        else if (res.status === 'expired') setPhase('invalid');
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err?.message || 'Approval link invalid.');
        setPhase('invalid');
      });
    return () => { cancelled = true; };
  }, [token]);

  const onApprove = async () => {
    setPhase('approving');
    setError(null);
    try {
      const res = await apiClient.request('/api/billing/hearts-approval/approve', {
        method: 'POST',
        body: JSON.stringify({ token, origin_url: window.location.origin }),
      });
      if (res.url) {
        window.location.href = res.url;
      } else {
        setError('Stripe did not return a checkout URL.');
        setPhase('ready');
      }
    } catch (err) {
      setError(err?.message || 'Could not start the payment. Please try again.');
      setPhase('ready');
    }
  };

  const onDecline = async () => {
    try {
      await apiClient.request('/api/billing/hearts-approval/decline', {
        method: 'POST',
        body: JSON.stringify({ token }),
      });
      setPhase('declined');
    } catch (err) {
      setError(err?.message || 'Could not decline. Please try again.');
    }
  };

  const amount = data ? `${(data.currency || 'GBP').toUpperCase()} ${((data.amount_pence || 0) / 100).toFixed(2)}` : '';

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-orange-50 py-12 px-4">
      <div className="max-w-md mx-auto bg-white rounded-3xl shadow-xl p-6 sm:p-8" data-testid="parent-approve-hearts-page">
        <img src="/hibff-logo.png" alt="HiBFF" className="h-12 mx-auto mb-6" />

        {phase === 'loading' && (
          <div className="text-center py-10">
            <Loader2 size={36} className="animate-spin mx-auto mb-3" style={{ color: PURPLE }} />
            <p className="text-gray-600 text-sm">Checking the request…</p>
          </div>
        )}

        {phase === 'invalid' && (
          <ResultPanel
            icon={<AlertTriangle size={28} style={{ color: '#B42318' }} />}
            iconBg="#FEF3F2"
            heading="This link can&apos;t be used"
            message={error || 'The approval request has expired or already been actioned.'}
          />
        )}

        {phase === 'declined' && (
          <ResultPanel
            icon={<X size={28} style={{ color: '#475467' }} />}
            iconBg="#F2F4F7"
            heading="Request declined"
            message="No problem — your teen has been told gently. Nothing was charged."
            testid="parent-approve-declined"
          />
        )}

        {phase === 'already-approved' && (
          <ResultPanel
            icon={<CheckCircle2 size={28} style={{ color: '#059669' }} />}
            iconBg="#ECFDF5"
            heading="Already approved"
            message="You already approved this request — your teen has been notified."
          />
        )}

        {(phase === 'ready' || phase === 'approving') && data && (
          <div data-testid="parent-approve-form">
            <div className="text-center mb-6">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full mb-3" style={{ background: '#FFF1F2' }}>
                <Heart size={28} fill="#FF6384" stroke="#FF6384" />
              </div>
              <h1 className="text-2xl font-extrabold text-gray-900 mb-1">
                {data.teen_name?.split(' ')[0] || 'Your teen'} is asking
              </h1>
              <p className="text-gray-600 text-sm">A one-off purchase, no subscription.</p>
            </div>

            <div className="rounded-2xl p-5 mb-4" style={{ background: 'linear-gradient(135deg, #FAF5FF 0%, #FFF7ED 100%)', border: '1px solid #EDE9FE' }}>
              <div className="flex items-baseline justify-between mb-2">
                <span className="text-sm text-gray-600">Hearts pack</span>
                <span className="text-xl font-bold text-gray-900" data-testid="approve-hearts-count">{data.hearts} ❤️</span>
              </div>
              <div className="flex items-baseline justify-between mb-2">
                <span className="text-sm text-gray-600">Total</span>
                <span className="text-xl font-bold text-gray-900" data-testid="approve-amount">{amount}</span>
              </div>
              <div className="pt-2 mt-2 border-t border-gray-200/60 flex items-baseline justify-between">
                <span className="text-[11px] uppercase tracking-wider text-gray-500 font-semibold">Bank statement</span>
                <span className="text-[12px] font-mono text-gray-700">{data.statement_descriptor}</span>
              </div>
            </div>

            {error && (
              <div className="mb-3 p-3 rounded-lg text-sm" style={{ background: '#FEF3F2', color: '#B42318', border: '1px solid #FDA29B' }} data-testid="parent-approve-error">{error}</div>
            )}

            <button
              onClick={onApprove}
              disabled={phase === 'approving'}
              className="w-full px-6 py-4 rounded-full font-bold text-white text-base mb-3 disabled:opacity-60 transition-transform hover:scale-[1.01] active:scale-[0.99]"
              style={{ background: `linear-gradient(135deg, ${ORANGE}, #FF9F4A)`, boxShadow: `0 4px 18px ${ORANGE}55` }}
              data-testid="parent-approve-btn"
            >
              {phase === 'approving'
                ? <><Loader2 size={16} className="inline animate-spin mr-2" /> Opening secure payment…</>
                : <>Approve &amp; pay {amount}</>}
            </button>
            <button
              onClick={onDecline}
              disabled={phase === 'approving'}
              className="w-full px-6 py-3 rounded-full font-semibold text-sm transition-colors"
              style={{ background: '#F2F4F7', color: '#344054' }}
              data-testid="parent-decline-btn"
            >
              Not this time
            </button>

            <p className="text-[11px] text-gray-500 text-center mt-5">
              Secure payment via Stripe. Cancel anytime · 30-day refund · 90-day pause apply to subscriptions only — this is a one-off.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

const ResultPanel = ({ icon, iconBg, heading, message, testid }) => (
  <div className="text-center py-8" data-testid={testid}>
    <div className="inline-flex items-center justify-center w-16 h-16 rounded-full mb-4" style={{ background: iconBg }}>
      {icon}
    </div>
    <h1 className="text-xl font-bold text-gray-900 mb-2" dangerouslySetInnerHTML={{ __html: heading }} />
    <p className="text-gray-600 max-w-sm mx-auto text-sm">{message}</p>
  </div>
);

export default ParentApproveHearts;
